import { useMemo, useState } from "react";
import { z } from "zod";
import {
  ALL_GROUP_KEY,
  Box,
  DataTable,
  DataTableCell,
  DataTableCellContent,
  DENSITY_CONFIG,
  Icon,
  Menu,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Text,
  Toggle,
  WidgetEmpty,
  WidgetSkeleton,
  useWidgetSurface,
  type DataTableColumnDef,
  type DataTableGroupType,
  type IconName,
  type WidgetModule,
  type WidgetRenderContext,
  type WidgetSettingsGroup,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import { GroupQueryProvider, useGroupStates } from "#app/lib/hooks/group-query-provider";
import { useIssueView } from "#app/components/issue-view";
import { type IssueRow, IssuesDocument } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { formatRelativeDate } from "#app/lib/utils/format-relative-date";
import { useTeamMemberInsights, type TeamMemberInsight } from "./use-team-member-insights";
import { useMemberProjects } from "./use-member-projects";

/**
 * `team-members` — a managed-only, team-scoped roster and work snapshot. Every
 * collapsed row shows real activity from the dashboard period; one row expands
 * inline into assigned-work metrics, workflow/type filters, and project links.
 * The expanded table is the member's OWN windowed paged query — it reaches every
 * assigned project, and its total matches the "Assigned projects" tile by
 * construction (both read the same server aggregation/predicate).
 */
export const teamMembersConfigSchema = z
  .looseObject({
    leadsFirst: z.boolean().default(true),
  });

export type TeamMembersConfig = z.infer<typeof teamMembersConfigSchema>;

/** Newest-touched first — module-level so the paged query variables stay stable. */
const PROJECT_ORDER = { lastUpdateDate: "desc" } as const;
const getIssuesConnection = (data: any) => data?.issues;

function MetricTile({ value, label }: { value: number; label: string }) {
  return (
    <Box flex={1} minWidth={104} paddingX="md" paddingY="sm">
      <Text size="lg" weight="bold">
        {value}
      </Text>
      <Text size="xs" variant="muted" truncate={1}>
        {label}
      </Text>
    </Box>
  );
}

function MemberFilterMenu({
  icon,
  label,
  allLabel,
  options,
  selectedId,
  onSelect,
}: {
  icon: IconName;
  label: string;
  allLabel: string;
  options: { id: string; label: string }[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const selected = options.find((option) => option.id === selectedId);
  return (
    <Menu>
      {/* A plain Toggle: icon + label always visible, `pressed` carrying the
        * selected wash. Clearing lives on the "All …" item; the chip has no ×
        * because a nested clear control cannot live inside a menu trigger. */}
      <MenuTrigger
        render={
          <Toggle
            aria-label={`${label} filter`}
            size="xs"
            variant="outline"
            pressed={selectedId !== null}
          />
        }
      >
        <Icon name={icon} size="sm" />
        <Text truncate={1}>{selected?.label ?? label}</Text>
      </MenuTrigger>
      <MenuPopup align="end">
        <MenuGroupLabel>{label}</MenuGroupLabel>
        <MenuSeparator />
        <MenuItem onClick={() => onSelect(null)}>
          <Icon name={selectedId === null ? "check" : "circle"} size="sm" color="muted" />
          <Text>{allLabel}</Text>
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.id} onClick={() => onSelect(option.id)}>
            <Icon name={selectedId === option.id ? "check" : "circle"} size="sm" color="muted" />
            <Text>{option.label}</Text>
          </MenuItem>
        ))}
      </MenuPopup>
    </Menu>
  );
}

function ExpandedMember({
  insight,
  wide,
  updatesByMemberProject,
}: {
  insight: TeamMemberInsight;
  wide: boolean;
  /** Per-(member, project) event counts keyed `"<userId>:<issueId>"`. */
  updatesByMemberProject: Record<string, number>;
}) {
  const { openIssue } = useIssueView();
  // The surface the enclosing widget card painted — the inner table's opaque
  // parts must blend with it.
  const widgetSurface = useWidgetSurface();
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [projectTypeId, setProjectTypeId] = useState<string | null>(null);
  const userId = insight.member.userId;
  // Exact per-member aggregations (tiles + filter menus) and the folded `where`
  // the paged table reads — nothing here derives from a capped sample.
  const { assignedTotal, workflows, projectTypes, tableWhere, error } = useMemberProjects(
    userId,
    workflowId,
    projectTypeId,
  );
  const { workflowMap } = useWorkspaceData();
  if (error) throw error;

  const columns = useMemo<DataTableColumnDef<IssueRow>[]>(() => {
    const result: DataTableColumnDef<IssueRow>[] = [
      {
        id: "name",
        header: "Project",
        accessorFn: (issue) => issue.name,
        meta: { minWidth: 180 },
        cell: ({ row }) => (
          <DataTableCellContent>
            <EntityTag
              entity={{
                kind: "issue",
                id: row.original.issueId,
                issue: {
                  name: row.original.name,
                  icon: row.original.icon,
                },
                workflowId: row.original.workflowId,
              }}
              display="label"
              weight="regular"
              interactive={false}
            />
          </DataTableCellContent>
        ),
      },
    ];
    if (wide) {
      result.push(
        {
          id: "workflow",
          header: "Workflow",
          accessorFn: (issue) => workflowMap.get(issue.workflowId)?.name ?? "Unknown workflow",
          meta: { minWidth: 140, maxWidth: 200 },
          cell: ({ row }) => (
            <DataTableCell>
              {workflowMap.get(row.original.workflowId)?.name ?? "Unknown workflow"}
            </DataTableCell>
          ),
        },
        {
          id: "type",
          header: "Project type",
          accessorFn: (issue) => issue.issueType?.name ?? "No project type",
          meta: { minWidth: 130, maxWidth: 180 },
          cell: ({ row }) => (
            <DataTableCell>{row.original.issueType?.name ?? "No project type"}</DataTableCell>
          ),
        },
      );
    }
    result.push({
      id: "activity",
      header: "Recent activity",
      accessorFn: (issue) => updatesByMemberProject[`${userId}:${issue.id}`] ?? 0,
      meta: { minWidth: 150, maxWidth: 190 },
      cell: ({ row }) => {
        const recentUpdates = updatesByMemberProject[`${userId}:${row.original.id}`] ?? 0;
        const activity =
          recentUpdates === 0
            ? "No recent updates"
            : `${recentUpdates} ${recentUpdates === 1 ? "update" : "updates"}`;
        const updated = row.original.lastUpdateDate
          ? formatRelativeDate(row.original.lastUpdateDate, "compact")
          : null;
        return (
          <DataTableCell align="end" textBrightness={recentUpdates === 0 ? "subtle" : "high"}>
            {updated ? `${activity} · ${updated}` : activity}
          </DataTableCell>
        );
      },
    });
    return result;
  }, [wide, workflowMap, updatesByMemberProject, userId]);

  // The member's projects page through their OWN windowed server query — the
  // DataTable's virtual scroll reaches every assigned row, never a slice.
  const { states, registerState, getGroupProps } = useGroupStates<IssueRow>();
  const tableState = states.get(ALL_GROUP_KEY);
  const tableTotal = tableState?.total ?? 0;
  const tableLoading = tableState?.loading ?? true;
  const projectGroups = useMemo<DataTableGroupType<IssueRow>[]>(
    () => [
      {
        key: ALL_GROUP_KEY,
        label: "Projects",
        totalCount: states.get(ALL_GROUP_KEY)?.total,
        ...getGroupProps(ALL_GROUP_KEY),
      },
    ],
    [states, getGroupProps],
  );
  // Internal scroll needs a bounded viewport: fit up to 8 rows exactly, then
  // clamp with a half-row peek so longer lists read as scrollable.
  const rowHeight = DENSITY_CONFIG.compact.rowHeight;
  const tableHeight = rowHeight + Math.min(Math.max(tableTotal, 1), 8.5) * rowHeight;

  return (
    // Box carries no margin props; the panel's inset is the one authored
    // className here.
    <Box
      marginX="md" marginTop="sm" marginBottom="md" direction="column"
      rounded="md"
      border
      overflow="hidden"
      surface="card"
    >
      <Box direction="column">
        <Box direction="row" wrap border="bottom" className="divide-x divide-border">
          <MetricTile value={assignedTotal} label="Assigned projects" />
          <MetricTile value={insight.updatedProjects} label="Projects updated" />
          <MetricTile value={insight.recentUpdates} label="Recent updates" />
          <MetricTile value={workflows.length} label="Workflows" />
        </Box>

        {/* paddingX matches the MetricTile row above so the filter chips share
            the panel's content gutter. */}
        <Box
          direction="row"
          align="center"
          justify="end"
          gap="sm"
          wrap
          paddingX="md"
          paddingY="sm"
          border="bottom"
        >
          <MemberFilterMenu
            icon="layers"
            label="Workflow"
            allLabel="All workflows"
            options={workflows}
            selectedId={workflowId}
            onSelect={setWorkflowId}
          />
          <MemberFilterMenu
            icon="tag"
            label="Type"
            allLabel="All project types"
            options={projectTypes}
            selectedId={projectTypeId}
            onSelect={setProjectTypeId}
          />
        </Box>
      </Box>

      <GroupQueryProvider<IssueRow>
        groupKey={ALL_GROUP_KEY}
        document={IssuesDocument}
        variables={{ where: tableWhere, orderBy: PROJECT_ORDER, limit: 50 }}
        getConnection={getIssuesConnection}
        onStateChange={registerState}
      />
      {!tableLoading && tableTotal === 0 ? (
        <Box padding="md" align="center" border="top">
          <Text size="sm" variant="muted">
            No projects match these filters.
          </Text>
        </Box>
      ) : (
        <Box direction="column" height={tableHeight}>
          <DataTable
            columns={columns}
            groups={projectGroups}
            getRowId={(issue) => issue.issueId}
            loading={tableLoading}
            surface={widgetSurface}
            fillColumnId="name"
            density="compact"
            hideGroupCount
            paddingX="md"
            paddingXAlign="content"
            onRowClick={(row) => openIssue(row.original as unknown as IssueRow)}
          />
        </Box>
      )}
    </Box>
  );
}

function TeamMembersRender({ w, config }: WidgetRenderContext<TeamMembersConfig>) {
  const { isTeamScope, insights, updatesByMemberProject, loading } = useTeamMemberInsights(
    config.leadsFirst,
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const wide = w >= 6;

  if (!isTeamScope) {
    return (
      <WidgetEmpty
        icon="users"
        title="Scope to a team"
        description="This widget shows members when the dashboard is scoped to a team."
      />
    );
  }
  if (loading) {
    return <WidgetSkeleton variant="rows" />;
  }
  if (insights.length === 0) {
    return <WidgetEmpty icon="users" title="No members yet" />;
  }

  return (
    <Box direction="column">
      {insights.map((insight) => {
        const expanded = expandedId === insight.member.userId;
        return (
          <Box key={insight.member.userId} border="bottom">
            <Box
              as="button"
              direction="row"
              align="center"
              gap="sm"
              paddingX="md"
              paddingY="sm"
              fill="width"
              cursor="pointer"
              hoverBackground
              background={expanded ? "selected" : undefined}
              onClick={() =>
                setExpandedId((current) => (current === insight.member.userId ? null : insight.member.userId))
              }
              aria-label={`${expanded ? "Collapse" : "Expand"} ${insight.member.name ?? insight.member.email}`}
              aria-expanded={expanded}
            >
              <Icon name={expanded ? "chevron-down" : "chevron-right"} size="xs" color="muted" />
              <Box fill="flex" shrink minWidth={0}>
                <EntityTag
                  entity={{
                    kind: "user",
                    id: insight.member.userId,
                    user: insight.member,
                  }}
                  subtitle={wide}
                  weight={expanded ? "semibold" : "medium"}
                  interactive={false}
                />
              </Box>
              <Box direction="column" align="end" gap="none" shrink={false}>
                <Text size="sm" weight="semibold">
                  {insight.updatedProjects}
                </Text>
                <Text size="xs" variant="muted">
                  {wide ? "projects updated" : "updated"}
                </Text>
              </Box>
              {wide ? (
                <Box direction="column" width={72} align="end" gap="none" shrink={false}>
                  <Text size="sm" weight="semibold">
                    {insight.recentUpdates}
                  </Text>
                  <Text size="xs" variant="muted">
                    updates
                  </Text>
                </Box>
              ) : null}
            </Box>
            {expanded ? (
              <ExpandedMember
                insight={insight}
                wide={wide}
                updatesByMemberProject={updatesByMemberProject}
              />
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

const teamMembersSettings: WidgetSettingsGroup[] = [
  {
    title: "Display",
    fields: [
      {
        key: "leadsFirst",
        kind: "toggle",
        label: "Show leads first",
        icon: "award",
      },
    ],
  },
];

export const teamMembersDef: WidgetModule<TeamMembersConfig> = {
  type: "team-members",
  title: "Team members",
  icon: "users",
  span: { minW: 2, minH: 2, maxW: 12, maxH: 24, defaultW: 4, defaultH: 6 },
  autoHeight: true,
  bleed: true,
  configSchema: teamMembersConfigSchema,
  settings: teamMembersSettings,
  render: TeamMembersRender,
};
